import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Home, 
  TrendingUp, 
  RefreshCw, 
  Shield, 
  Building2, 
  Minimize2,
  ArrowRight,
  Star,
  Trash2,
  Edit,
} from 'lucide-react';
import { SCENARIO_TEMPLATES, type ScenarioTemplate, templateToScenarios } from '@/utils/supreme-intelligence/scenarioTemplates';
import { usePersonalTemplates } from '@/hooks/usePersonalTemplates';
import type { PersonalTemplate, ScenarioInput } from '@/types/supremeIntelligenceTypes';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface TemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectTemplate: (scenarios: ScenarioInput[]) => void;
}

const categoryIcons = {
  buyer: Home,
  investor: TrendingUp,
  refinance: RefreshCw,
  special: Shield,
};

const categoryLabels = {
  buyer: 'Home Buyers',
  investor: 'Investors',
  refinance: 'Refinancing',
  special: 'Special Programs',
};

export const TemplateDialog: React.FC<TemplateDialogProps> = ({
  open,
  onOpenChange,
  onSelectTemplate,
}) => {
  const [selectedCategory, setSelectedCategory] = useState<ScenarioTemplate['category']>('buyer');
  const { templates, deleteTemplate } = usePersonalTemplates();
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const filteredTemplates = SCENARIO_TEMPLATES.filter(
    template => template.category === selectedCategory
  );

  const filteredPersonalTemplates = templates.filter(
    template => template.category === selectedCategory
  );

  const handleSelectDefaultTemplate = (template: ScenarioTemplate) => {
    const scenarios = templateToScenarios(template);
    onSelectTemplate(scenarios);
    onOpenChange(false);
  };

  const handleSelectPersonalTemplate = async (templateId: string) => {
    const { loadTemplate } = usePersonalTemplates();
    const template = await loadTemplate(templateId);
    if (template) {
      onSelectTemplate(template.scenarios);
      onOpenChange(false);
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    await deleteTemplate(templateId);
    setDeleteId(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Choose a Scenario Template</DialogTitle>
          <DialogDescription>
            Start with pre-built scenarios tailored to common situations
          </DialogDescription>
        </DialogHeader>

        <Tabs value={selectedCategory} onValueChange={(v) => setSelectedCategory(v as ScenarioTemplate['category'])}>
          <TabsList className="grid w-full grid-cols-4">
            {Object.entries(categoryLabels).map(([key, label]) => {
              const Icon = categoryIcons[key as keyof typeof categoryIcons];
              return (
                <TabsTrigger key={key} value={key} className="gap-2">
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{label}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {Object.keys(categoryLabels).map((category) => (
            <TabsContent key={category} value={category} className="mt-4 space-y-4">
              {/* Personal Templates Section */}
              {filteredPersonalTemplates.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Star className="w-4 h-4 text-primary" />
                    <h3 className="font-semibold text-sm">Your Templates</h3>
                    <Badge variant="secondary" className="ml-auto">
                      {filteredPersonalTemplates.length}
                    </Badge>
                  </div>
                  
                  <div className="grid gap-4 md:grid-cols-2">
                    {filteredPersonalTemplates.map((template) => (
                      <Card
                        key={template.id}
                        className="hover:shadow-lg transition-shadow group border-primary/20"
                      >
                        <CardHeader>
                          <div className="flex items-start justify-between">
                            <div className="space-y-1 flex-1">
                              <div className="flex items-center gap-2">
                                <CardTitle className="text-lg group-hover:text-primary transition-colors">
                                  {template.name}
                                </CardTitle>
                                <Badge variant="outline" className="text-xs">Custom</Badge>
                              </div>
                              {template.description && (
                                <CardDescription className="text-sm">
                                  {template.description}
                                </CardDescription>
                              )}
                            </div>
                            <div className="flex gap-1">
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteId(template.id);
                                }}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8"
                                onClick={() => handleSelectPersonalTemplate(template.id)}
                              >
                                <ArrowRight className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <Badge variant="secondary">
                            {template.scenarioCount} Scenarios
                          </Badge>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Default Templates Section */}
              <div className="space-y-3">
                {filteredPersonalTemplates.length > 0 && (
                  <div className="flex items-center gap-2 pt-4 border-t">
                    <h3 className="font-semibold text-sm">Default Templates</h3>
                  </div>
                )}
                
                <div className="grid gap-4 md:grid-cols-2">
                  {filteredTemplates.map((template) => (
                    <Card
                      key={template.id}
                      className="hover:shadow-lg transition-shadow cursor-pointer group"
                      onClick={() => handleSelectDefaultTemplate(template)}
                    >
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="space-y-1">
                            <CardTitle className="text-lg group-hover:text-primary transition-colors">
                              {template.name}
                            </CardTitle>
                            <CardDescription className="text-sm">
                              {template.description}
                            </CardDescription>
                          </div>
                          <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary">
                              {template.scenarios.length} Scenarios
                            </Badge>
                          </div>
                          
                          <div className="space-y-1.5">
                            {template.scenarios.map((scenario, idx) => (
                              <div
                                key={idx}
                                className="flex items-center justify-between text-xs p-2 rounded bg-muted/50"
                              >
                                <span className="font-medium">{scenario.name}</span>
                                <div className="flex gap-2 text-muted-foreground">
                                  <span>{scenario.downPaymentPercentage}% down</span>
                                  <span>•</span>
                                  <span>{scenario.interestRate.toFixed(2)}% APR</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {filteredTemplates.length === 0 && filteredPersonalTemplates.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <p>No templates available in this category</p>
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </div>
      </DialogContent>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this template? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteId && handleDeleteTemplate(deleteId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};
